import React, { useState, useEffect } from 'react';
import { Shield, CheckCircle, AlertTriangle, ExternalLink } from 'lucide-react';
import { onboardingIntegrationClient } from '../../../api/onboardingIntegrationClient';

interface MagicLinkIntegrationProps {
  projectId: string;
  userRole: 'client' | 'vendor' | 'admin';
}

export const MagicLinkIntegration: React.FC<MagicLinkIntegrationProps> = ({ 
  projectId, 
  userRole 
}) => {
  const [magicLink, setMagicLink] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string>('');

  const generateLink = async () => {
    setIsGenerating(true);
    setError('');
    
    try {
      const result = await onboardingIntegrationClient.generateMagicLink({
        projectId,
        clientEmail: 'client@example.com',
        expiresIn: '7d'
      });
      const link = result.url;
      setMagicLink(link);
    } catch (err) {
      setError('Failed to generate magic link');
      console.error('Magic link generation error:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(magicLink);
  };

  return (
    <div className="p-6 rounded-xl bg-white border border-gray-200 shadow-lg">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <Shield className="w-5 h-5 text-[#4267A1] mr-2" />
        Magic Link Authentication
      </h3>
      
      <div className="space-y-4">
        <p className="text-gray-600">
          Generate secure, no-login access links for clients to access their onboarding portal.
        </p>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={generateLink}
            disabled={isGenerating || userRole === 'client'}
            className="px-4 py-2 bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white rounded-lg hover:shadow-lg hover:shadow-[#4267A1]/25 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {isGenerating ? 'Generating...' : 'Generate Magic Link'}
          </button>
          
          {userRole === 'client' && (
            <span className="text-sm text-gray-500">
              Only available to project managers
            </span>
          )}
        </div>
        
        {magicLink && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-green-800">Magic Link Generated</span>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={magicLink}
                readOnly
                className="flex-1 p-2 text-sm bg-white border border-green-300 rounded"
              />
              <button
                onClick={copyToClipboard}
                className="px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
              >
                Copy
              </button>
              <a
                href={magicLink}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center space-x-1"
              >
                <ExternalLink className="w-3 h-3" />
                <span>Test</span>
              </a>
            </div>
          </div>
        )}
        
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              <span className="text-sm text-red-800">{error}</span>
            </div>
          </div>
        )}
        
        <div className="text-xs text-gray-500">
          <p>• Magic links expire after 7 days</p>
          <p>• Links provide client-only access to project data</p>
          <p>• All client actions are logged for audit purposes</p>
        </div>
      </div>
    </div>
  );
};
